import React, { FC, useContext } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { NavLink } from 'react-router-dom';

import { Row, Column } from '../../../../../../components/grid/Grid';
import { MaterialIcon } from '../../../../../../components/material-icon/MaterialIcon';
import { Hyperlink } from '../../../../../../components/Hyperlink';

import { LegislationContext } from '../../../LegislationContext';

import { useIsElementVisible } from '../../hooks/useIsElementVisible';
import { doctrineAnalytics } from '../../../../../../utils/analyticsUtils';
import { transformCount } from '../../utils/utils';

import styles from './navigation.module.scss';

type Props = {
  containerClassName?: string;
  show?: boolean;
};

export const Navigation: FC<Props> = ({ containerClassName = '', show }) => {
  const {
    legislationInfosAll,
    commentaries,
    decisions,
    preparatoryWork,
    articleId,
    initialVersionId,
    textNature,
    legislationInfosPreviousAndNext: {
      legislationInfosPrevious,
      legislationInfosNext,
    },
  } = useContext(LegislationContext);

  const { isVisible: isVisibleCommentaries } = useIsElementVisible(
    'commentaries',
  );
  const { isVisible: isVisibleText } = useIsElementVisible('text');
  const { isVisible: isVisibleDecisions } = useIsElementVisible('decisions');
  const { isVisible: isVisiblePreparatoryWork } = useIsElementVisible(
    'preparatory-work',
  );

  const hasPreviousArticle = Boolean(legislationInfosPrevious?.articleId);
  const hasNextArticle = Boolean(legislationInfosNext?.articleId);

  const onNavigationClick = (type: string) => {
    doctrineAnalytics('Legislation HeaderItemClicked', {
      type,
      article_version_id: articleId,
      article_id: initialVersionId,
      legislationType: textNature,
    });
  };

  return (
    <Row
      className={classNames(styles.container, {
        [containerClassName]: Boolean(containerClassName),
      })}
    >
      <Column className={styles.content}>
        <ol className={styles.list}>
          <li className={classNames(styles.item)}>
            <NavLink
              isActive={() => isVisibleText}
              className={styles.itemLink}
              activeClassName={styles.itemActive}
              to={(location) => ({ ...location, hash: 'text' })}
              onClick={() => onNavigationClick('Text')}
            >
              <FormattedMessage
                id="page/components/Navigation.tsx/1"
                values={{
                  count: legislationInfosAll.length,
                }}
              />
            </NavLink>
          </li>
          {!!commentaries?.data?.hits?.length && (
            <li className={classNames(styles.item)}>
              <NavLink
                isActive={() => isVisibleCommentaries}
                className={styles.itemLink}
                activeClassName={styles.itemActive}
                to={(location) => ({ ...location, hash: 'commentaries' })}
                onClick={() => onNavigationClick('Commentaries')}
              >
                <FormattedMessage
                  id="page/components/Navigation.tsx/2"
                  values={{
                    count: transformCount(commentaries.data.totalHits),
                  }}
                />
              </NavLink>
            </li>
          )}
          <li className={classNames(styles.item)}>
            <NavLink
              isActive={() => isVisibleDecisions}
              className={styles.itemLink}
              activeClassName={styles.itemActive}
              to={(location) => ({ ...location, hash: 'decisions' })}
              onClick={() => onNavigationClick('Decisions')}
            >
              <FormattedMessage
                id="page/components/Navigation.tsx/3"
                values={{
                  count: transformCount(decisions?.data?.totalHits ?? 0),
                }}
              />
            </NavLink>
          </li>
          {!!preparatoryWork?.data?.hits?.length && (
            <li className={classNames(styles.item)}>
              <NavLink
                isActive={() => isVisiblePreparatoryWork}
                className={styles.itemLink}
                activeClassName={styles.itemActive}
                to={(location) => ({ ...location, hash: 'preparatory-work' })}
                onClick={() => onNavigationClick('Preparatory work')}
              >
                <FormattedMessage
                  id="page/components/Navigation.tsx/4"
                  values={{
                    count: transformCount(preparatoryWork.data.totalHits),
                  }}
                />
              </NavLink>
            </li>
          )}
          {show && hasPreviousArticle && (
            <li
              className={classNames(styles.item, {
                [styles.navigationItem]: hasPreviousArticle,
              })}
            >
              <Hyperlink
                href={legislationInfosPrevious?.url}
                source="previous_article"
                className={styles.navigationLink}
              >
                <MaterialIcon
                  name="keyboard_arrow_left"
                  className={styles.navigationIcon}
                />
                <span>
                  <FormattedMessage
                    id="page/components/ContentHeaderTopComponent.tsx/1"
                    values={{ number: legislationInfosPrevious?.articleNumber }}
                  />
                </span>
              </Hyperlink>
            </li>
          )}
          {show && hasNextArticle && (
            <li
              className={classNames(styles.item, {
                [styles.navigationItem]: !hasPreviousArticle,
              })}
            >
              <Hyperlink
                href={legislationInfosNext?.url}
                source="next_article"
                className={styles.navigationLink}
              >
                <span>
                  <FormattedMessage
                    id="page/components/ContentHeaderTopComponent.tsx/1"
                    values={{ number: legislationInfosNext?.articleNumber }}
                  />
                </span>
                <MaterialIcon
                  name="keyboard_arrow_right"
                  className={styles.navigationIcon}
                />
              </Hyperlink>
            </li>
          )}
        </ol>
      </Column>
    </Row>
  );
};
